import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { folderService, projectService, fetchWithSessionHandling } from '../services/api';
import { useAuth } from './AuthContext';

const ProjectContext = createContext();

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const navigate = useNavigate();
  const { projectId, tab, folderId } = useParams();
  const location = useLocation();
  const { user } = useAuth(); // Get authenticated user
  
  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState({ shared: [], personal: [], orphanedShared: [] });
  const [currentProject, setCurrentProject] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(null); // For folder browsing mode
  const [activeTab, setActiveTab] = useState('chat');
  const [loading, setLoading] = useState(true);

  // Determine current mode from URL
  const isCrossProjectMode = location.pathname === '/cross-projects';
  const isConfigurationMode = location.pathname === '/configuration';
  const isDashboardMode = location.pathname === '/dashboard';
  const isFolderBrowsingMode = location.pathname === '/' || location.pathname.startsWith('/folders');

  // Load folders and projects from API on mount and when user changes
  useEffect(() => {
    loadFoldersAndProjects();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper to find folder by ID in the folder tree
  const findFolderById = useCallback((folderId, folderList) => {
    for (const folder of folderList) {
      if (folder.id === folderId) return folder;
      if (folder.children) {
        const found = findFolderById(folderId, folder.children);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Sync current project and tab from URL params
  useEffect(() => {
    if (projectId && projects.length > 0) {
      const project = projects.find(p => p.id === projectId);
      if (project) {
        setCurrentProject(project);
        setCurrentFolder(null); // Clear folder when viewing a project
        setActiveTab(tab || 'chat');
      }
    }
  }, [projectId, tab, projects]);

  // Sync current folder from URL params
  useEffect(() => {
    if (folderId && folders) {
      const allFolders = [...(folders.shared || []), ...(folders.personal || [])];
      const folder = findFolderById(folderId, allFolders);
      
      if (folder) {
        setCurrentFolder(folder);
        setCurrentProject(null); // Clear project when viewing a folder
      }
    } else if (isFolderBrowsingMode && !folderId) {
      // Root level folder view
      setCurrentFolder(null);
      setCurrentProject(null);
    }
  }, [folderId, folders, isFolderBrowsingMode, findFolderById]);

  const loadFoldersAndProjects = async () => {
    try {
      // Use authenticated user's ID for personal workspace
      const userId = user?.id || null;
      
      const response = await folderService.getFoldersAndProjects(userId);
      const data = response.data;
      
      setFolders({
        shared: data.shared || [],
        personal: data.personal || [],
        orphanedShared: data.orphanedShared || []
      });

      // Flatten all projects from folders and orphaned
      const allProjects = [
        ...extractProjectsFromFolders(data.shared || []),
        ...extractProjectsFromFolders(data.personal || []),
        ...(data.orphanedShared || [])
      ];
      
      setProjects(allProjects);
      
      if (allProjects.length > 0 && !currentProject) {
        setCurrentProject(allProjects[0]);
      }
    } catch (error) {
      console.error('Error loading folders and projects:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to recursively extract projects from folder hierarchy
  const extractProjectsFromFolders = (folderList) => {
    let projects = [];
    
    folderList.forEach(folder => {
      // Add projects from this folder
      if (folder.projects) {
        projects = projects.concat(folder.projects);
      }
      
      // Recursively add projects from child folders
      if (folder.children && folder.children.length > 0) {
        projects = projects.concat(extractProjectsFromFolders(folder.children));
      }
    });
    
    return projects;
  };

  const createProject = async (name, folderId = null) => {
    try {
      const response = await fetchWithSessionHandling('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, folderId })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create project');
      }

      const newProject = await response.json();
      
      // Reload folders and projects to get updated structure
      await loadFoldersAndProjects();
      
      // Navigate to the new project's configuration tab to set up project details
      navigate(`/projects/${newProject.id}/config`);
      
      return newProject;
    } catch (error) {
      throw error;
    }
  };

  const deleteProject = async (projectId, options = {}) => {
    try {
      const response = await fetchWithSessionHandling(`/api/projects/${projectId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete project');
      }

      // Reload folders and projects
      await loadFoldersAndProjects();
      
      if (!options.skipNavigate) {
        // If we deleted the current project, switch to another one
        if (currentProject?.id === projectId) {
          const remainingProjects = projects.filter(p => p.id !== projectId);
          if (remainingProjects.length > 0) {
            navigate(`/projects/${remainingProjects[0].id}/chat`);
          } else {
            navigate('/cross-projects');
          }
        }
      }
      
      return true;
    } catch (error) {
      throw error;
    }
  };

  const createFolder = async (name, parentId = null, isPersonal = false) => {
    try {
      // Use authenticated user's ID for personal folders
      const userId = isPersonal ? user?.id : null;
      
      const response = await folderService.createFolder({
        name,
        parentId,
        userId,
        isPersonal
      });
      
      // Reload folders and projects
      await loadFoldersAndProjects();
      
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const updateFolder = async (folderId, name) => {
    try {
      await folderService.updateFolder(folderId, { name });
      
      // Reload folders and projects
      await loadFoldersAndProjects();
    } catch (error) {
      throw error;
    }
  };

  const deleteFolder = async (folderId) => {
    try {
      await folderService.deleteFolder(folderId);
      
      // Reload folders and projects
      await loadFoldersAndProjects();
    } catch (error) {
      throw error;
    }
  };

  const moveFolder = async (folderId, newParentId) => {
    try {
      await folderService.moveFolder(folderId, newParentId);
      
      // Reload folders and projects
      await loadFoldersAndProjects();
    } catch (error) {
      throw error;
    }
  };

  const moveProject = async (projectId, folderId) => {
    try {
      if (!folderId) {
        throw new Error('Please select a folder to move the project to');
      }

      await projectService.updateProject(projectId, { folderId });

      // Reload folders and projects to reflect the change
      await loadFoldersAndProjects();
    } catch (error) {
      console.error('Error moving project:', error);
      // Throw the error with the message from the backend if available
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  };

  const switchToProject = (projectId, navigateToConfig = false) => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      const targetTab = navigateToConfig ? 'config' : 'chat';
      navigate(`/projects/${projectId}/${targetTab}`);
    }
  };

  const switchToCrossProject = () => {
    navigate('/cross-projects');
  };

  const switchToConfiguration = () => {
    navigate('/configuration');
  };

  const selectFolder = (folder) => {
    // Navigate to folder view route
    if (folder) {
      navigate(`/folders/${folder.id}`);
    } else {
      navigate('/'); // Root folder view
    }
  };

  const value = {
    projects,
    folders,
    currentProject,
    currentFolder,
    activeTab,
    isCrossProjectMode,
    isConfigurationMode,
    isDashboardMode,
    isFolderBrowsingMode,
    loading,
    setActiveTab,
    switchToProject,
    switchToCrossProject,
    switchToConfiguration,
    selectFolder,
    findFolderById,
    createProject,
    deleteProject,
    createFolder,
    updateFolder,
    deleteFolder,
    moveFolder,
    moveProject,
    loadProjects: loadFoldersAndProjects // Alias for compatibility
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};